// Mirrors new-server/src/constants/notification-types.js — keep both in sync
// when adding a type. Drives the Notifications preferences page generically
// (one card per type, looped) instead of hand-coded per-type form blocks.
//
// `group` buckets types thematically for that preferences page — the forest
// itself is what this platform is about, so it's ordered first; blockchain
// plumbing is the least interesting to a normal user, so it's last.
export const NOTIFICATION_GROUPS = [
  { key: 'forest', label: 'Forest & management', description: 'Everything about the forests themselves — new forests, growth scans, management tasks and contracts.' },
  { key: 'marketplace', label: 'Marketplace', description: 'Offers and trades on the ZCO2 marketplace.' },
  { key: 'customers', label: 'Customers & retirement', description: 'Customer accounts and CO2 retirement activity.' },
  { key: 'blockchain', label: 'Blockchain & infrastructure', description: 'Wallet balances, RPC health and on-chain transfers.' }
]

export const NOTIFICATION_TYPES = [
  {
    key: 'new_area',
    label: 'New forest added',
    description: 'A new forest was registered on the platform.',
    audience: 'administrator',
    group: 'forest',
    icon: 'fa-tree'
  },
  {
    key: 'new_checkpoint',
    label: 'New growth scan for your forest',
    description: 'A new growth checkpoint was added for one of your forests.',
    audience: 'user',
    group: 'forest',
    icon: 'fa-seedling'
  },
  {
    key: 'forest_task_pending_admin',
    label: 'Forest management task pending',
    description: 'A forest management task is new or overdue.',
    audience: 'administrator',
    group: 'forest',
    icon: 'fa-clipboard-list'
  },
  {
    key: 'forest_task_pending_user',
    label: 'Your forest has a pending task',
    description: 'One of your forests has a forest management task that needs attention.',
    audience: 'user',
    group: 'forest',
    icon: 'fa-clipboard-list'
  },
  {
    key: 'contract_expiring',
    label: 'Forest management contract expiring soon',
    description: 'A forest management contract is within 30 days of its expiry date.',
    audience: 'administrator',
    group: 'forest',
    icon: 'fa-file-contract'
  },
  {
    key: 'marketplace_offer_created',
    label: 'New marketplace offer created',
    description: 'A seller listed a new ZCO2 offer on the marketplace.',
    audience: 'administrator',
    group: 'marketplace',
    icon: 'fa-store'
  },
  {
    key: 'marketplace_offer_purchased',
    label: 'Your offer was purchased',
    description: 'Someone bought ZCO2 from one of your marketplace offers.',
    audience: 'user',
    group: 'marketplace',
    icon: 'fa-hand-holding-dollar'
  },
  {
    key: 'marketplace_purchase_completed',
    label: 'Your purchase is complete',
    description: 'A ZCO2 purchase you made on the marketplace was confirmed on-chain.',
    audience: 'user',
    group: 'marketplace',
    icon: 'fa-basket-shopping'
  },
  {
    key: 'new_customer',
    label: 'New customer created',
    description: 'A new customer account was created.',
    audience: 'administrator',
    group: 'customers',
    icon: 'fa-user-plus'
  },
  {
    key: 'tokens_retired',
    label: 'Tokens retired',
    description: 'A customer retired ZCO2 tokens.',
    audience: 'administrator',
    group: 'customers',
    icon: 'fa-leaf'
  },
  {
    key: 'retire_completed',
    label: 'Your CO2 retirement is complete',
    description: 'Your token retirement was confirmed and a certificate is ready.',
    audience: 'user',
    group: 'customers',
    icon: 'fa-certificate'
  },
  {
    key: 'withdraw_requested',
    label: 'Withdraw requested',
    description: 'A withdrawal of forest tokens was requested.',
    audience: 'administrator',
    group: 'customers',
    icon: 'fa-arrow-right-from-bracket'
  },
  {
    key: 'low_wallet_balance',
    label: 'Low master wallet balance',
    description: 'A master wallet (main or MyTreeInitiative) has dropped below its configured minimum xDai balance.',
    audience: 'administrator',
    group: 'blockchain',
    icon: 'fa-wallet'
  },
  {
    key: 'rpc_down',
    label: 'Blockchain RPC unreachable',
    description: 'A configured JSON-RPC provider failed its health check.',
    audience: 'administrator',
    group: 'blockchain',
    icon: 'fa-plug-circle-xmark'
  },
  {
    key: 'low_zco2_stock',
    label: 'Low ZCO2 token stock',
    description: 'The "Available" ZCO2 balance shown in the header has dropped below your configured minimum.',
    audience: 'administrator',
    group: 'blockchain',
    icon: 'fa-coins'
  },
  {
    key: 'transfer_failed',
    label: 'Blockchain transfer failed',
    description: 'A queued blockchain transfer moved to failed status.',
    audience: 'administrator',
    group: 'blockchain',
    icon: 'fa-triangle-exclamation'
  }
]

export const LEVEL_ICON = {
  info: 'fa-circle-info',
  warning: 'fa-triangle-exclamation',
  critical: 'fa-circle-exclamation'
}
